/**
 * Auto-recovery for stale-bundle chunk-load failures.
 *
 * When the dashboard is redeployed, the SPA's hashed chunk filenames change and
 * the previous ones are deleted from the server. A page that's already open then
 * fails to lazy-load a route chunk ("Failed to fetch dynamically imported
 * module"). Rather than crashing to the error screen mid-assessment, we reload
 * ONCE to pull the fresh index + chunks.
 *
 * Guarded so a genuinely-missing chunk or an offline state can't loop: we only
 * auto-reload if we haven't already tried within RELOAD_WINDOW_MS. If we tried
 * recently and it still failed, the caller shows the normal error screen.
 */

const RELOAD_KEY = 'prism:chunk-reload-at';
const RELOAD_WINDOW_MS = 10_000;

const CHUNK_ERROR_RE =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|dynamically imported module|ChunkLoadError|Loading chunk [\w-]+ failed/i;

/** True when an error looks like a lazy-chunk fetch failure (stale bundle). */
export function isChunkLoadError(err: unknown): boolean {
  if (!err) return false;
  const msg =
    typeof err === 'string'
      ? err
      : ((err as { message?: string }).message ?? String(err));
  return CHUNK_ERROR_RE.test(msg);
}

/**
 * Reload once to fetch the fresh bundle. Returns true if a reload was scheduled,
 * false if we already tried within the guard window (so the caller can fall back
 * to showing the error screen instead of looping).
 */
export function reloadOnceForChunkError(): boolean {
  try {
    const now = Date.now();
    const last = Number(window.sessionStorage.getItem(RELOAD_KEY) || 0);
    if (now - last < RELOAD_WINDOW_MS) return false; // already tried — don't loop
    window.sessionStorage.setItem(RELOAD_KEY, String(now));
  } catch {
    // sessionStorage disabled — fall through and still attempt a single reload.
  }
  window.location.reload();
  return true;
}

/**
 * Register the Vite preload-error listener — the primary path. Vite dispatches
 * `vite:preloadError` on window when a dynamic import fails; preventDefault()
 * stops it throwing while we reload. Call once at app boot.
 */
export function installChunkReloadHandler(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener('vite:preloadError', (event: Event) => {
    event.preventDefault();
    reloadOnceForChunkError();
  });
}
